import { useCallback, useState } from 'react'
import { useQuery, useMutation } from 'react-query'
import GRADE_LEVELS from '../../constants/grade-levels'
import useCacheKeys from '../use-cache-keys'
import useCache from '../use-cache'
import useApi from '../use-api'
import { pick } from 'lodash'

const defaultNewCourse = {
    gradeLevels: Object.values(GRADE_LEVELS),
    date: new Date(),
    minCapacity: 4,
    maxCapacity: 10,
    credits: 1,
    enrollCutoff: 1,
    dropCutoff: 1,
    ['private']: true,
}

const useCourseMutation = ({
    courseId,
    onSuccess = () => {},
    onError = () => {},
    saveAsNew
}) => {
    const [ course, setCourse ] = useState(defaultNewCourse)
    const cacheKeys = useCacheKeys()
    const { clearCache } = useCache({ key: cacheKeys.COURSES, onReload: onSuccess})
    const { api, endpoints, url } = useApi()
    const updateProperties = [
        'name',
        'description',
        'facilitatorId',
        'date',
        'locationId',
        'hourId',
        'durationId',
        'minCapacity',
        'maxCapacity',
        'credits',
        'dimensions',
        'gradeLevels',
        'requirements',
        'private',
        'enrollCutoff',
        'dropCutoff',
        'isSecretEnrollment',
        'rollOutDate',
    ]
    const createProperties = [
        'name',
        'description',
        'facilitatorId',
        'locationId',
        'minCapacity',
        'maxCapacity',
        'credits',
        'dimensions',
        'gradeLevels',
        'requirements',
        'occurrences',
        'isSeries',
        'private',
        'enrollCutoff',
        'dropCutoff',
        'isSecretEnrollment',
        'rollOutDate',
    ]

    //  Fetching
    const onFetchSuccess = useCallback(course => setCourse({ ...course, ['private']: saveAsNew ? true : course.private }), [setCourse])

    //  Load our course we are working with (if ID is presented)
    const { isLoading } = useQuery(
        [ cacheKeys.COURSES, courseId ],
        () => api.get(url(endpoints.course, { courseId })),
        { enabled: Boolean(courseId), onSuccess: onFetchSuccess }
    )

    const [ create, { isLoading: isCreating }] = useMutation(
        () => api.post(endpoints.courses, pick(course, createProperties)),
        { onSuccess: clearCache, onError }
    )
    const [ update, { isLoading: isUpdating }] = useMutation(
        () => api.put(url(endpoints.course, { courseId }), pick(course, updateProperties)),
        { onSuccess: clearCache, onError }
    )

    //  Saving
    const save = useCallback(() => {
        if (course.id && !saveAsNew) {
            update()
        } else {
            create()
        }
    }, [course, update, create])


    return {
        isBusy: isLoading || isCreating || isUpdating,
        isLoading,
        isSaving: isCreating || isUpdating,
        save,
        course,
        setCourse,
    }
}

export default useCourseMutation